import { lazy, Suspense } from 'react'
import { BrowserRouter, Link, Route, Routes, useLocation } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { AppShell } from '@/components/layout/AppShell'
import { Spinner } from '@/components/ui/Spinner'
import { ease } from '@/lib/motion'
import { ViewerHost } from '@/components/viewer/DocumentViewer'
import { ChatPage } from '@/pages/ChatPage'
import { DocumentsPage } from '@/pages/DocumentsPage'

const AdminPage = lazy(() => import('@/pages/AdminPage'))

function NotFound() {
  return (
    <main id="main" className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
      <h1 className="text-lg font-semibold">Page not found</h1>
      <Link to="/" className="text-sm font-medium text-accent-text hover:underline">
        Back to chat
      </Link>
    </main>
  )
}

function PageFallback() {
  return (
    <div className="flex flex-1 items-center justify-center text-ink-muted" role="status">
      <Spinner className="size-5" label="Loading" />
    </div>
  )
}

/**
 * Pages cross-fade between sections (chat ⇄ documents ⇄ admin). Each page gets its own
 * frozen `location`, so a page that is fading out keeps rendering the route it
 * was on instead of re-matching the new URL (which would re-run redirects).
 * Chat routes share one section, so moving between chats never remounts the page.
 */
function AnimatedRoutes() {
  const location = useLocation()
  const section = location.pathname.startsWith('/admin')
    ? 'admin'
    : location.pathname.startsWith('/documents')
      ? 'documents'
      : 'chat'
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={section}
        className="flex min-w-0 flex-1"
        // Opacity only: scaling a whole page re-rasterises all of its text every frame.
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18, ease: ease.out }}
      >
        <Routes location={location}>
          <Route path="/" element={<ChatPage />} />
          <Route path="/c/:id" element={<ChatPage />} />
          <Route path="/documents/:id?" element={<DocumentsPage />} />
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<PageFallback />}>
                <AdminPage />
              </Suspense>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  )
}

export function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppShell>
        <AnimatedRoutes />
        <ViewerHost />
      </AppShell>
    </BrowserRouter>
  )
}
